import { useEffect, useRef } from 'react';

interface QueueModalProps {
  /** YouTube id of the trailer to play. */
  videoId: string;
  /** Where the explore button goes — the landing site. */
  exploreUrl: string;
  onClose: () => void;
}

/**
 * The studio is not open to everyone yet, so "Open the studio" opens this
 * instead: the trailer, where the person stands, and a way back out.
 */
export default function QueueModal({ videoId, exploreUrl, onClose }: QueueModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // The dialog scrolls itself; the page behind it must not.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}` +
    '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-title"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-head">
          <span className="mono">In the queue</span>
          <button ref={closeRef} className="modal-x" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="frame">
          <iframe
            src={src}
            title="Frameon trailer"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>

        <h2 id="queue-title" className="d modal-title">
          You&rsquo;re in the queue.
        </h2>
        <p className="sub">
          Seats open in batches, and your invitation is sent when yours comes up. Keep an eye on your
          inbox — that is where you&rsquo;ll hear from us.
        </p>

        <div className="actions">
          <a className="btn" href={exploreUrl}>
            Explore →
          </a>
        </div>
      </div>
    </div>
  );
}
